import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../ui/Icon';
import { AudioFX } from '../ui/primitives';
import {
  StockItemView,
  StockRun,
  cancelStockRun,
  createStockRun,
  openStockPdf,
  plural,
  printStockRun,
  printStockSummary,
  recordStockMovement,
  resetTimeLabel,
  settleStockRun,
  stockErrorCode,
  stockErrorText,
  undoStockBatch,
  untilLabel,
  useInvalidateStock,
  useStockOverview,
} from './api';
import {
  Confirm,
  HistorySheet,
  ItemPickerSheet,
  ItemSheet,
  Keypad,
  KeypadRequest,
  Ring,
  itemIconName,
  itemTitle,
  itemVariant,
  signed,
  timePl,
} from './parts';
import './magazyn.css';

/*
 * Tablica Magazynu — wspólna dla kiosku stacji i panelu admina (desktop + telefon).
 *
 * Tryby: Stan (podgląd) · Dostawa (poranne wpisanie chleba) · Dobranie (lista „Do dobrania"
 * i jej rozliczenie po powrocie z piekarni) · Zdejmij (ręczne zdjęcie z powodem) ·
 * Inwentaryzacja (policzone sztuki). Szkice każdego trybu trzymamy osobno w sessionStorage —
 * przełączenie trybu ani przypadkowe odświeżenie nie gubi wpisanych liczb.
 */

export type BoardMode = 'stock' | 'delivery' | 'pick' | 'remove' | 'count';

export interface BoardPrintRequest {
  title: string;
  subtitle?: string;
  /** Zleca wydruk; zwraca zadanie druku (null = nic do drukowania) i komunikat. */
  run: () => Promise<{ jobId: string | null; message: string }>;
}

export interface MagazynBoardProps {
  /** Gęstość i rozmiar dotyku: kiosk ≥64 px, admin ≥36 px. */
  variant: 'kiosk' | 'admin';
  /** Wąski ekran (telefon) — jedna kolumna. */
  compact?: boolean;
  /** Wydruk przez agenta: kiosk → ekran drukowania (usePrint), admin → toast. */
  onPrint: (req: BoardPrintRequest) => void;
  notify: (msg: string, type?: 'ok' | 'err' | 'info') => void;
  /** Kiosk: pełnoekranowy błysk + dźwięk po zapisie. */
  flash?: (kind: 'ok' | 'err', msg: string, sub?: string) => void;
  /** Admin: pokaż przyciski „PDF" (podgląd/druk z przeglądarki, bez agenta). */
  allowPdf?: boolean;
  /** Niezapisany szkic (dostawa/zdjęcie/inwentaryzacja/rozliczenie) — kiosk wstrzymuje wtedy auto-wylogowanie i przeładowanie. */
  onDirtyChange?: (dirty: boolean) => void;
  initialMode?: BoardMode;
  /** Zmiana trybu (np. do zapamiętania w adresie). */
  onModeChange?: (mode: BoardMode) => void;
}

/* ---------- szkice ---------- */

type Qty = Record<string, number>;
type PickDraft = { runId: string; qty: Qty; off: string[]; extra: Qty };
type Drafts = { delivery: Qty; remove: Qty; count: Qty; reason: string | null; pick: PickDraft | null };

const DRAFT_KEY = 'packflow.magazyn.drafts.v1';
const SORT_KEY = 'packflow.magazyn.sort';
const EMPTY: Drafts = { delivery: {}, remove: {}, count: {}, reason: null, pick: null };

function loadDrafts(): Drafts {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return EMPTY;
    const d = JSON.parse(raw);
    return {
      delivery: d.delivery || {},
      remove: d.remove || {},
      count: d.count || {},
      reason: d.reason || null,
      pick: d.pick && d.pick.runId ? { runId: d.pick.runId, qty: d.pick.qty || {}, off: d.pick.off || [], extra: d.pick.extra || {} } : null,
    };
  } catch {
    return EMPTY;
  }
}

const sumQty = (q: Qty) => Object.keys(q).reduce((s, k) => s + (q[k] || 0), 0);
const countQty = (q: Qty) => Object.keys(q).filter((k) => (q[k] || 0) > 0).length;
const pickDirty = (p: PickDraft | null) => !!p && (Object.keys(p.qty).length > 0 || p.off.length > 0 || Object.keys(p.extra).length > 0);

/** Kod listy jak na wydruku: 6 pierwszych znaków id bez kresek, wielkie litery. */
export const runCode = (id: string) => id.replace(/-/g, '').slice(0, 6).toUpperCase();

/* ---------- statusy pozycji ---------- */

type Tone = 'short' | 'tight' | 'ok' | 'none';

function itemStatus(it: StockItemView, demandKnown: boolean): { tone: Tone; label: string } {
  if (!demandKnown) return { tone: 'none', label: it.onHand > 0 ? 'Na stanie' : 'Brak' };
  if (it.shortage > 0) return { tone: 'short', label: `Brakuje ${it.shortage}` };
  if (it.demand === 0) {
    if (it.toTake > 0) return { tone: 'tight', label: `Mało · dobierz ${it.toTake}` };
    return { tone: 'none', label: 'Bez zamówień' };
  }
  if (it.low || it.toTake > 0) return { tone: 'tight', label: it.toTake > 0 ? `Mało · dobierz ${it.toTake}` : 'Mało' };
  if (it.free <= 0) return { tone: 'tight', label: 'Na styk' };
  return { tone: 'ok', label: `Wolne ${it.free}` };
}

const isShort = (i: StockItemView) => i.shortage > 0 || i.toTake > 0;

type Filter = 'active' | 'short' | 'all';
type Sort = 'own' | 'az' | 'short';
const SORT_LABEL: Record<Sort, string> = { own: 'Moja kolejność', az: 'A–Z', short: 'Braki najpierw' };
const SORT_NEXT: Record<Sort, Sort> = { own: 'short', short: 'az', az: 'own' };

const MODES: Array<{ id: BoardMode; label: string; short: string; icon: string }> = [
  { id: 'stock', label: 'Stan', short: 'Stan', icon: 'warehouse' },
  { id: 'delivery', label: 'Dostawa', short: 'Dostawa', icon: 'truck' },
  { id: 'pick', label: 'Dobranie', short: 'Dobranie', icon: 'clipboard-list' },
  { id: 'remove', label: 'Zdejmij', short: 'Zdejmij', icon: 'package-minus' },
  { id: 'count', label: 'Inwentaryzacja', short: 'Spis', icon: 'scale' },
];

type UndoState = { batchId: string; text: string; at: number };
type Banner = { tone: 'short' | 'ok'; text: string; cta?: boolean } | null;

const UNDO_MS = 15000;

/* =================================================================== */

export default function MagazynBoard(props: MagazynBoardProps): JSX.Element {
  const { variant, compact, onPrint, notify, flash, allowPdf, onDirtyChange } = props;
  const kiosk = variant === 'kiosk';
  const ov = useStockOverview();
  const invalidate = useInvalidateStock();
  const data = ov.data;

  const [mode, setModeState] = useState<BoardMode>(props.initialMode || 'stock');
  const [drafts, setDrafts] = useState<Drafts>(loadDrafts);
  const [filter, setFilter] = useState<Filter>('active');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>(() => {
    try {
      const s = localStorage.getItem(SORT_KEY) as Sort | null;
      return s && SORT_LABEL[s] ? s : 'own';
    } catch {
      return 'own';
    }
  });
  const [inc, setInc] = useState<number | 'pack'>(1);
  const [keypad, setKeypad] = useState<KeypadRequest | null>(null);
  const [sheet, setSheet] = useState<null | { kind: 'history' } | { kind: 'item'; key: string } | { kind: 'picker' }>(null);
  const [confirm, setConfirm] = useState<null | 'remove' | 'cancelRun' | 'clear'>(null);
  const [busy, setBusyState] = useState(false);
  // Ref obok stanu: dwa szybkie dotknięcia „Zapisz" w tym samym takcie widzą jeszcze busy=false
  // ze starego renderu — ref blokuje drugi zapis od razu (inaczej dostawa poszłaby dwa razy).
  const busyRef = useRef(false);
  const lock = () => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusyState(true);
    return true;
  };
  const setBusy = (v: boolean) => { busyRef.current = v; setBusyState(v); };
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [banner, setBanner] = useState<Banner>(null);
  const [pulse, setPulse] = useState<{ key: string; n: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [scanVal, setScanVal] = useState('');
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Wynik zapisu (np. „Brakuje jeszcze 15 szt.") ma być widoczny — przewiń do niego.
  useEffect(() => {
    if (banner && bannerRef.current) bannerRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [banner]);

  const sfx = useCallback((k: 'beep' | 'buzz' | 'success' | 'click') => { if (kiosk) AudioFX[k](); }, [kiosk]);

  const setMode = useCallback((m: BoardMode) => {
    setModeState(m);
    props.onModeChange?.(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onModeChange]);

  /* --- trwałość szkiców + sygnał „brudny" --- */
  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(drafts)); } catch { /* prywatne okno */ }
  }, [drafts]);
  const dirty = countQty(drafts.delivery) > 0 || countQty(drafts.remove) > 0 || Object.keys(drafts.count).length > 0 || pickDirty(drafts.pick);
  // Przez ref: rodzic może podać nową funkcję przy każdym renderze — sygnał idzie tylko przy zmianie.
  const dirtyCb = useRef(onDirtyChange);
  dirtyCb.current = onDirtyChange;
  const dirtyRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (dirtyRef.current === dirty) return;
    dirtyRef.current = dirty;
    dirtyCb.current?.(dirty);
  }, [dirty]);
  useEffect(() => () => { dirtyCb.current?.(false); }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  /* --- pasek „Cofnij" znika po 15 s --- */
  useEffect(() => {
    if (!undo) return;
    const left = UNDO_MS - (Date.now() - undo.at);
    const id = setTimeout(() => setUndo(null), Math.max(0, left));
    return () => clearTimeout(id);
  }, [undo]);

  useEffect(() => {
    if (data?.config?.quickSteps?.length && typeof inc === 'number' && data.config.quickSteps.indexOf(inc) < 0) setInc(data.config.quickSteps[0]);
  }, [data?.config?.quickSteps, inc]);
  // Zdejmij pokazuje tylko 3 pierwsze kroki (bez „Opakowanie") — krok wybrany w Dostawie, którego
  // tu nie widać, odejmowałby niewidoczną wartość. Przełącz na pierwszy widoczny.
  useEffect(() => {
    if (mode !== 'remove') return;
    const shown = (data?.config?.quickSteps?.length ? data.config.quickSteps : [1, 5]).slice(0, 3);
    if (typeof inc !== 'number' || shown.indexOf(inc) < 0) setInc(shown[0]);
  }, [mode, data?.config?.quickSteps, inc]);

  // Pierwszy powód zdjęcia z konfiguracji, jeśli nic nie wybrano.
  useEffect(() => {
    const reasons = data?.config?.removalReasons || [];
    if (reasons.length && (!drafts.reason || reasons.indexOf(drafts.reason) < 0)) setDrafts((d) => ({ ...d, reason: reasons[0] }));
  }, [data?.config?.removalReasons, drafts.reason]);

  // Szkic rozliczenia należy do konkretnej listy — po nowej liście / rozliczeniu gdzie indziej zerujemy.
  const openRun = data?.openRun || null;
  useEffect(() => {
    if (!data) return;
    if (drafts.pick && (!openRun || openRun.id !== drafts.pick.runId)) setDrafts((d) => ({ ...d, pick: null }));
  }, [data, openRun, drafts.pick]);

  const items = useMemo(() => data?.items || [], [data]);
  const byKey = useMemo(() => {
    const m: Record<string, StockItemView> = {};
    items.forEach((i) => { m[i.key] = i; });
    return m;
  }, [items]);
  const demandKnown = !!data?.demandAvailable;

  const refresh = useCallback(async () => {
    invalidate();
    const r = await ov.refetch();
    return r.data;
  }, [invalidate, ov]);

  /* --- lista widocznych pozycji --- */
  const draftForMode: Qty = useMemo(
    () => (mode === 'delivery' ? drafts.delivery : mode === 'remove' ? drafts.remove : mode === 'count' ? drafts.count : {}),
    [mode, drafts.delivery, drafts.remove, drafts.count],
  );
  const needle = q.trim().toLowerCase();
  const searched = useMemo(
    () =>
      items.filter((i) =>
        !needle ||
        i.name.toLowerCase().indexOf(needle) >= 0 ||
        (i.sku || '').toLowerCase().indexOf(needle) >= 0 ||
        (i.barcode || '').toLowerCase().indexOf(needle) >= 0,
      ),
    [items, needle],
  );
  const counts = {
    active: searched.filter((i) => i.active).length,
    short: searched.filter(isShort).length,
    all: searched.length,
  };
  const visible = useMemo(() => {
    const list = searched.filter((i) => {
      if (draftForMode[i.key] !== undefined) return true; // pozycja ze szkicu nie może zniknąć z widoku
      if (filter === 'active') return i.active;
      if (filter === 'short') return isShort(i);
      return true;
    });
    if (sort === 'az') return list.slice().sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    if (sort === 'short') {
      const idx: Record<string, number> = {};
      items.forEach((i, n) => { idx[i.key] = n; });
      return list.slice().sort((a, b) => b.toTake - a.toTake || b.shortage - a.shortage || idx[a.key] - idx[b.key]);
    }
    return list;
  }, [searched, filter, sort, items, draftForMode]);

  function changeSort() {
    const s = SORT_NEXT[sort];
    setSort(s);
    try { localStorage.setItem(SORT_KEY, s); } catch { /* bez pamięci */ }
  }

  /* --- szkice: zmiany --- */
  const stepOf = useCallback((it: StockItemView) => (inc === 'pack' ? Math.max(1, it.step || 1) : inc), [inc]);

  const setQty = useCallback((which: 'delivery' | 'remove' | 'count', key: string, value: number | null) => {
    setDrafts((d) => {
      const next = { ...d[which] };
      if (value === null || (which !== 'count' && value <= 0)) delete next[key];
      // API przyjmuje do 99 999 szt. na pozycję — dotknięcia kafelka nie mogą przebić limitu.
      else next[key] = Math.min(99999, Math.max(0, Math.round(value)));
      return { ...d, [which]: next };
    });
  }, []);

  const bumpPulse = useCallback((key: string) => setPulse((p) => ({ key, n: (p?.n || 0) + 1 })), []);

  const scrollTo = useCallback((key: string) => {
    setTimeout(() => {
      const el = document.getElementById(`mg-item-${key}`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 30);
  }, []);

  /* --- klawiatura dla pozycji --- */
  const openKeypadFor = useCallback((which: 'delivery' | 'remove' | 'count', it: StockItemView) => {
    const name = itemTitle(it);
    const v = itemVariant(it);
    const sub = v ? v : it.sku || undefined;
    if (which === 'count') {
      const cur = drafts.count[it.key] !== undefined ? drafts.count[it.key] : Math.max(0, it.onHand);
      setKeypad({
        title: `Policzone: ${name}`,
        sub,
        icon: itemIconName(it.icon),
        initial: cur,
        suggestions: [{ label: `Jak w systemie: ${it.onHand}`, value: Math.max(0, it.onHand) }, { label: 'Zero', value: 0 }],
        describe: (n) => {
          const d = n - it.onHand;
          return <>było <b className="tnum">{it.onHand}</b> → jest <b className="tnum">{n}</b> <span className={d === 0 ? '' : d > 0 ? 'plus' : 'minus'}>({signed(d)})</span></>;
        },
        confirmLabel: 'Zapisz wynik',
        onDone: (n) => { setQty('count', it.key, n); bumpPulse(it.key); },
      });
      return;
    }
    const cur = drafts[which][it.key] || 0;
    const sugg: Array<{ label: string; value: number }> = [];
    if (which === 'delivery' && it.toTake > 0) sugg.push({ label: `Brakuje ${it.toTake}`, value: it.toTake });
    if (which === 'delivery' && it.step > 1) sugg.push({ label: `Opak. ${it.step}`, value: it.step }, { label: `2 opak. ${it.step * 2}`, value: it.step * 2 });
    if (which === 'remove' && it.onHand > 0) sugg.push({ label: `Wszystko: ${it.onHand}`, value: it.onHand });
    setKeypad({
      title: which === 'delivery' ? `Dostawa: ${name}` : `Zdejmij: ${name}`,
      sub,
      icon: itemIconName(it.icon),
      initial: cur,
      suggestions: sugg,
      describe: (n) =>
        which === 'delivery'
          ? <>na stanie będzie <b className="tnum">{it.onHand + n}</b>{it.demand > 0 ? <> · potrzeba <b className="tnum">{it.demand}</b></> : null}</>
          : n > Math.max(0, it.onHand)
            ? <span className="minus">więcej niż na stanie ({it.onHand})</span>
            : <>zostanie <b className="tnum">{it.onHand - n}</b></>,
      onDone: (n) => { setQty(which, it.key, n); bumpPulse(it.key); },
    });
  }, [drafts, setQty, bumpPulse]);

  /* --- skaner kodów --- */
  const handleScan = useCallback((raw: string) => {
    const code = raw.trim().toLowerCase();
    if (!code) return;
    const it = items.find((i) => (i.sku && i.sku.toLowerCase() === code) || (i.barcode && i.barcode.toLowerCase() === code));
    if (!it) {
      sfx('buzz');
      notify(`Nieznany kod: ${raw.trim()}`, 'err');
      return;
    }
    sfx('beep');
    if (mode === 'count') {
      openKeypadFor('count', it);
    } else if (mode === 'delivery' || mode === 'remove') {
      setQty(mode, it.key, (drafts[mode][it.key] || 0) + stepOf(it));
    } else {
      setSheet({ kind: 'item', key: it.key });
    }
    bumpPulse(it.key);
    scrollTo(it.key);
  }, [items, mode, drafts, stepOf, setQty, openKeypadFor, bumpPulse, scrollTo, notify, sfx]);

  // Skaner pisze jak klawiatura i kończy Enterem. Pole skanowania działa jak w sesji pakowania
  // (form + Enter + powrót fokusu), ale NIE łapiemy fokusu automatycznie — klawiatura ekranowa
  // zakryłaby kafelki. Zamiast tego szybkie serie znaków bez fokusu też trafiają do skanu.
  const scanFn = useRef(handleScan);
  scanFn.current = handleScan;
  const blocked = !!keypad || !!sheet || !!confirm;
  useEffect(() => {
    if (blocked) return;
    let buf = '';
    let last = 0;
    const onKey = (e: KeyboardEvent) => {
      const a = document.activeElement as HTMLElement | null;
      if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) return;
      const t = Date.now();
      const gap = t - last;
      last = t;
      if (e.key === 'Enter') {
        if (gap > 250) buf = '';
        // Faza capture + stopPropagation: Enter skanera nie „klika" kafelka, który ma fokus.
        if (buf.length >= 3) { e.preventDefault(); e.stopPropagation(); scanFn.current(buf); }
        buf = '';
        return;
      }
      // Skaner „pisze" znak co kilka ms; dłuższa przerwa = człowiek przy klawiaturze → od nowa.
      if (gap > 60) buf = '';
      if (e.key.length === 1) buf += e.key;
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [blocked]);

  function onScanSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleScan(scanVal);
    setScanVal('');
    if (!compact) scanRef.current?.focus();
  }

  /* --- zapis ruchów --- */
  // Po zapisie zdejmujemy ze szkicu tylko to, co poszło — dotknięcia w trakcie zapisu zostają.
  function dropSaved(which: 'delivery' | 'remove' | 'count', lines: Array<{ key: string; quantity: number }>) {
    setDrafts((d) => {
      const next = { ...d[which] };
      lines.forEach((l) => {
        if (which === 'count') {
          if (next[l.key] === l.quantity) delete next[l.key];
          return;
        }
        const left = (next[l.key] || 0) - l.quantity;
        if (left > 0) next[l.key] = left;
        else delete next[l.key];
      });
      return { ...d, [which]: next };
    });
  }

  function afterSave(text: string, batchId: string) {
    sfx('success');
    if (flash) flash('ok', text);
    else notify(text, 'ok');
    setUndo({ batchId, text, at: Date.now() });
  }

  async function saveDelivery() {
    const lines = Object.keys(drafts.delivery)
      .filter((k) => byKey[k] && drafts.delivery[k] > 0)
      .map((k) => ({ key: k, quantity: drafts.delivery[k] }));
    if (!lines.length || !lock()) return;
    try {
      const r = await recordStockMovement({ kind: 'delivery', lines });
      dropSaved('delivery', lines);
      afterSave(`Przyjęto ${r.units} szt.`, r.batchId);
      const fresh = await refresh();
      const missing = fresh?.totals.toTake || 0;
      // Bez UCS zapotrzebowanie jest nieznane — „wszystko pokryte" byłoby nieprawdą.
      setBanner(!fresh?.demandAvailable ? null : missing > 0
        ? { tone: 'short', text: `Brakuje jeszcze ${missing} szt.`, cta: true }
        : { tone: 'ok', text: 'Wszystkie zamówienia pokryte' });
      setMode('stock');
    } catch (e) {
      sfx('buzz');
      notify(stockErrorText(e), 'err');
    } finally {
      setBusy(false);
    }
  }

  async function saveRemove() {
    setConfirm(null);
    const lines = Object.keys(drafts.remove)
      .filter((k) => byKey[k] && drafts.remove[k] > 0)
      .map((k) => ({ key: k, quantity: drafts.remove[k] }));
    if (!lines.length || !drafts.reason || !lock()) return;
    try {
      const r = await recordStockMovement({ kind: 'remove', lines, reason: drafts.reason });
      dropSaved('remove', lines);
      afterSave(`Zdjęto ${r.units} szt. · ${drafts.reason}`, r.batchId);
      await refresh();
    } catch (e) {
      sfx('buzz');
      notify(stockErrorText(e), 'err');
    } finally {
      setBusy(false);
    }
  }

  async function saveCount() {
    const lines = Object.keys(drafts.count).filter((k) => byKey[k]).map((k) => ({ key: k, quantity: drafts.count[k] }));
    if (!lines.length || !lock()) return;
    try {
      const r = await recordStockMovement({ kind: 'adjust', lines });
      dropSaved('count', lines);
      if (r.applied) {
        afterSave(`Korekta: ${r.applied} ${plural(r.applied, ['pozycja', 'pozycje', 'pozycji'])}`, r.batchId);
      } else {
        // Bez różnic serwer nie zapisuje ruchu — nie ma czego cofać (Cofnij dałoby 404).
        sfx('success');
        notify('Stan się zgadzał — bez zmian', 'ok');
      }
      await refresh();
    } catch (e) {
      sfx('buzz');
      notify(stockErrorText(e), 'err');
    } finally {
      setBusy(false);
    }
  }

  async function doUndo() {
    if (!undo) return;
    const u = undo;
    setUndo(null);
    try {
      await undoStockBatch(u.batchId);
      notify('Cofnięto', 'info');
      setBanner(null);
      await refresh();
    } catch (e) {
      notify(stockErrorText(e), 'err');
    }
  }

  function fillByDemand() {
    const next: Qty = { ...drafts.delivery };
    let n = 0;
    items.forEach((it) => {
      if (it.toTake > 0) { next[it.key] = it.toTake; n += it.toTake; }
    });
    if (!n) { notify('Niczego nie brakuje — nie ma czego wypełniać', 'info'); return; }
    sfx('click');
    setDrafts((d) => ({ ...d, delivery: next }));
    setFilter('active');
  }

  function clearDraft(which: 'delivery' | 'remove' | 'count') {
    setDrafts((d) => ({ ...d, [which]: {} }));
    setConfirm(null);
  }

  /* --- dobranie --- */
  const pickLines = useMemo(() => items.filter((i) => i.toTake > 0), [items]);
  const pickTotal = pickLines.reduce((s, i) => s + i.toTake, 0);

  function printPickList() {
    let createdRunId: string | null = null;
    onPrint({
      title: 'Do dobrania',
      subtitle: `${pickTotal} szt. · ${pickLines.length} poz.`,
      // „Drukuj ponownie" na ekranie druku woła run() jeszcze raz — wtedy drukujemy TĘ SAMĄ
      // listę, zamiast tworzyć nową (z nowym kodem) w miejsce właśnie wydrukowanej.
      run: async () => {
        // Panel admina woła run() od razu — podwójne kliknięcie utworzyłoby drugą listę w miejsce pierwszej.
        if (!lock()) return { jobId: null, message: 'Lista „Do dobrania” już się drukuje' };
        try {
          if (createdRunId) return await printStockRun(createdRunId);
          const r = await createStockRun(true);
          createdRunId = r.run ? r.run.id : null;
          return { jobId: r.jobId, message: r.message };
        } finally {
          setBusy(false);
          invalidate();
        }
      },
    });
    setBanner(null);
  }

  async function createRunNoPrint() {
    if (!lock()) return;
    try {
      const r = await createStockRun(false);
      await refresh();
      if (r.run) {
        notify(r.message, 'ok');
        openStockPdf({ kind: 'pick', runId: r.run.id }).catch((e) => notify(stockErrorText(e), 'err'));
      } else notify(r.message, 'info');
    } catch (e) {
      notify(stockErrorText(e), 'err');
    } finally {
      setBusy(false);
    }
  }

  const pd: PickDraft | null = openRun ? (drafts.pick && drafts.pick.runId === openRun.id ? drafts.pick : { runId: openRun.id, qty: {}, off: [], extra: {} }) : null;
  const updatePick = useCallback((fn: (p: PickDraft) => PickDraft) => {
    if (!openRun) return;
    setDrafts((d) => {
      const base = d.pick && d.pick.runId === openRun.id ? d.pick : { runId: openRun.id, qty: {}, off: [], extra: {} };
      return { ...d, pick: fn(base) };
    });
  }, [openRun]);

  const settleLines = (run: StockRun, p: PickDraft) => {
    const lines = run.lines.map((l) => ({
      key: l.key,
      quantity: p.off.indexOf(l.key) >= 0 ? 0 : p.qty[l.key] !== undefined ? p.qty[l.key] : l.quantity,
    }));
    Object.keys(p.extra).forEach((k) => { if (p.extra[k] > 0) lines.push({ key: k, quantity: p.extra[k] }); });
    return lines;
  };

  async function settle() {
    if (!openRun || !pd || !lock()) return;
    try {
      const r = await settleStockRun(openRun.id, settleLines(openRun, pd));
      setDrafts((d) => ({ ...d, pick: null }));
      sfx('success');
      const text = `Przyjęto do stanu ${r.units} szt.`;
      if (flash) flash('ok', text, `Lista #${runCode(openRun.id)}`);
      else notify(text, 'ok');
      const fresh = await refresh();
      const missing = fresh?.totals.toTake || 0;
      setBanner(!fresh?.demandAvailable ? null : missing > 0
        ? { tone: 'short', text: `Brakuje jeszcze ${missing} szt. — nie wszystko udało się wziąć albo doszły zamówienia`, cta: true }
        : { tone: 'ok', text: 'Wszystkie zamówienia pokryte' });
      setMode('stock');
    } catch (e) {
      sfx('buzz');
      notify(stockErrorText(e), 'err');
      // 409 run_closed: listę rozliczono/zastąpiono na innej stacji — odśwież, żeby zniknęła.
      if (stockErrorCode(e) === 'run_closed') invalidate();
    } finally {
      setBusy(false);
    }
  }

  async function cancelRun() {
    setConfirm(null);
    if (!openRun) return;
    try {
      await cancelStockRun(openRun.id);
      setDrafts((d) => ({ ...d, pick: null }));
      notify('Lista anulowana', 'info');
      await refresh();
    } catch (e) {
      notify(stockErrorText(e), 'err');
    }
  }

  /* --- przejście z arkusza produktu do trybu --- */
  function itemAction(it: StockItemView, m: 'delivery' | 'remove' | 'count') {
    setSheet(null);
    setMode(m);
    if (!it.active && filter !== 'all') setFilter('all');
    scrollTo(it.key);
    bumpPulse(it.key);
    openKeypadFor(m, it);
  }

  /* =================== stany brzegowe =================== */

  if (ov.isLoading && !data) return <BoardSkeleton variant={variant} compact={compact} />;

  if (ov.isError && !data) {
    const code = stockErrorCode(ov.error);
    const status = (ov.error as any)?.response?.status;
    if (code === 'stock_disabled' || status === 404) return <Disabled variant={variant} />;
    if (code === 'stock_not_migrated') {
      return (
        <div className={`mg-board mg-${variant}`}>
          <div className="mg-empty">
            <span className="mg-empty-ico warn"><Icon name="triangle-alert" size={30} /></span>
            <h3>Magazyn czeka na aktualizację bazy</h3>
            <p>Brakuje migracji tabel magazynu. Administrator systemu musi ją uruchomić — potem wszystko zadziała od razu.</p>
          </div>
        </div>
      );
    }
    return (
      <div className={`mg-board mg-${variant}`}>
        <div className="mg-empty">
          <span className="mg-empty-ico err"><Icon name="circle-alert" size={30} /></span>
          <h3>Nie udało się wczytać magazynu</h3>
          <p>{stockErrorText(ov.error)}</p>
          <button type="button" className="mg-btn primary" onClick={() => ov.refetch()}><Icon name="rotate-ccw" size={18} /> Spróbuj ponownie</button>
        </div>
      </div>
    );
  }

  if (!data) return <BoardSkeleton variant={variant} compact={compact} />;
  if (!data.enabled) return <Disabled variant={variant} />;

  const t = data.totals;
  // Bez UCS zapotrzebowanie jest nieznane — nie pokazujemy „do dobrania" z zerowego popytu.
  const toTake = demandKnown ? t.toTake : 0;
  const sheetItem = sheet && sheet.kind === 'item' ? byKey[sheet.key] : null;
  const draftCounts: Record<BoardMode, number> = {
    stock: 0,
    delivery: countQty(drafts.delivery),
    pick: pickDirty(drafts.pick) ? 1 : 0,
    remove: countQty(drafts.remove),
    count: Object.keys(drafts.count).length,
  };

  const windowStartLabel = data.windowStart ? timePl(data.windowStart) : null;

  /* =================== render =================== */

  return (
    <div className={`mg-board mg-${variant}${compact ? ' mg-compact' : ''} mg-m-${mode}`}>
      <div className="mg-scroll">
        {/* Pasek stanu dnia */}
        <div className="mg-strip">
          <span className="mg-strip-i"><Icon name="clock" size={14} /> Stan od <b>{windowStartLabel || 'początku'}</b></span>
          <span className="mg-strip-sep" />
          {data.resetEnabled ? (
            <span className="mg-strip-i"><Icon name="rotate-ccw" size={14} /> reset o <b>{resetTimeLabel(data.resetTime)}</b>{data.nextResetAt && <span className="mg-strip-dim"> ({untilLabel(data.nextResetAt, now)})</span>}</span>
          ) : (
            <span className="mg-strip-i"><Icon name="rotate-ccw" size={14} /> bez codziennego resetu</span>
          )}
          {ov.isFetching && <span className="mg-live" title="Odświeżanie" />}
        </div>

        {!data.demandAvailable && (
          <div className="mg-note warn">
            <Icon name="cloud-off" size={18} />
            <div><b>Brak połączenia z UCS</b> — zapotrzebowanie nieznane, stan i dostawy działają.</div>
          </div>
        )}

        {!data.stockKnown && mode === 'stock' && (
          <div className="mg-hero">
            <span className="mg-hero-ico"><Icon name="sunrise" size={kiosk ? 34 : 28} /></span>
            <div className="mg-hero-text">
              <h2>Dzień dobry!</h2>
              <p>Wpisz pierwszą dostawę — od razu policzę, czego brakuje do zamówień.</p>
            </div>
            <button type="button" className="mg-btn success lg" onClick={() => setMode('delivery')}><Icon name="truck" size={20} /> Wpisz dostawę</button>
          </div>
        )}

        {banner && (
          <div className={`mg-banner ${banner.tone}`} ref={bannerRef}>
            <Icon name={banner.tone === 'ok' ? 'circle-check' : 'clipboard-list'} size={22} />
            <span className="mg-banner-t">{banner.text}</span>
            {banner.cta && (
              <button type="button" className="mg-btn danger" onClick={() => { setBanner(null); setMode('pick'); }}>
                {openRun
                  ? <><Icon name="clipboard-check" size={18} /> Rozlicz listę #{runCode(openRun.id)}</>
                  : <><Icon name="printer" size={18} /> Drukuj listę „Do dobrania”</>}
              </button>
            )}
            <button type="button" className="mg-x" onClick={() => setBanner(null)} aria-label="Zamknij"><Icon name="x" size={18} /></button>
          </div>
        )}

        {/* KPI */}
        <div className={`mg-kpis${mode === 'stock' ? '' : ' slim'}`}>
          <div className="mg-kpi hero">
            <div className="mg-ring-wrap">
              <Ring value={demandKnown ? t.ordersOk : 0} total={demandKnown ? t.ordersTotal : 0} size={mode !== 'stock' ? 34 : kiosk && !compact ? 66 : 54} stroke={mode !== 'stock' ? 5 : 7} />
              <span className="mg-ring-pct tnum">{demandKnown && t.ordersTotal ? `${Math.round((t.ordersOk / t.ordersTotal) * 100)}%` : ''}</span>
            </div>
            <div className="mg-kpi-body">
              <div className="mg-kpi-lbl">{compact && mode !== 'stock' ? 'Gotowe' : 'Gotowe do spakowania'}</div>
              <div className="mg-kpi-val tnum">{demandKnown ? <>{t.ordersOk}<span className="mg-of">/{t.ordersTotal}</span></> : '—'}</div>
              {demandKnown && (t.ordersPartial > 0 || t.ordersShort > 0) && (
                <div className="mg-dots">
                  {t.ordersPartial > 0 && <span><i className="d warn" />{t.ordersPartial} częściowo</span>}
                  {t.ordersShort > 0 && <span><i className="d err" />{t.ordersShort} brak</span>}
                </div>
              )}
            </div>
          </div>
          <div className="mg-kpi">
            <div className="mg-kpi-lbl"><Icon name="warehouse" size={14} /> Na stanie</div>
            <div className="mg-kpi-val tnum"><Bump v={t.onHand} /><span className="mg-unit">szt.</span></div>
            <div className="mg-kpi-sub">dziś +{t.delivered + t.restocked} · −{t.consumed + t.removed}</div>
          </div>
          <div className="mg-kpi">
            <div className="mg-kpi-lbl"><Icon name="clipboard-list" size={14} /> Potrzeba</div>
            <div className="mg-kpi-val tnum">{demandKnown ? <><Bump v={t.demand} /><span className="mg-unit">szt.</span></> : '—'}</div>
            <div className="mg-kpi-sub">{demandKnown ? `${t.ordersTotal} ${plural(t.ordersTotal, ['zamówienie', 'zamówienia', 'zamówień'])}` : 'UCS niedostępny'}</div>
          </div>
          <button type="button" className={`mg-kpi click ${toTake > 0 ? 'short' : demandKnown ? 'ok' : ''}`} onClick={() => setMode('pick')}>
            <div className="mg-kpi-lbl"><Icon name={toTake > 0 ? 'badge-alert' : demandKnown ? 'badge-check' : 'clipboard-list'} size={14} /> {compact ? 'Dobrać' : 'Do dobrania'}</div>
            {toTake > 0 ? (
              <div className="mg-kpi-val tnum"><Bump v={toTake} /><span className="mg-unit">szt.</span></div>
            ) : (
              <div className={`mg-kpi-val${demandKnown ? ' ok-text' : ''}`}>{demandKnown ? 'Wszystko jest' : '—'}</div>
            )}
            <div className="mg-kpi-sub">{openRun ? `lista #${runCode(openRun.id)} do rozliczenia` : toTake > 0 ? `${pickLines.length} ${plural(pickLines.length, ['pozycja', 'pozycje', 'pozycji'])}` : demandKnown ? 'zapas pokrywa zamówienia' : 'UCS niedostępny'}</div>
          </button>
        </div>

        {/* Tryby + narzędzia */}
        <div className="mg-modebar">
          <div className="mg-seg" role="tablist">
            {MODES.map((m) => {
              const badge = m.id === 'pick'
                ? (toTake > 0 ? String(toTake) : openRun ? '•' : '')
                : draftCounts[m.id] > 0 ? String(draftCounts[m.id]) : '';
              return (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={mode === m.id}
                  className={`mg-seg-b${mode === m.id ? ' on' : ''}`}
                  onClick={() => { sfx('click'); setMode(m.id); }}
                >
                  <Icon name={m.icon} size={compact ? 19 : kiosk ? 20 : 17} />
                  <span>{compact ? m.short : m.label}</span>
                  {badge && <span className={`mg-seg-badge${m.id === 'pick' ? (toTake > 0 ? ' err' : ' info') : ' draft'}`}>{badge}</span>}
                </button>
              );
            })}
          </div>
          <div className="mg-tools">
            <button type="button" className="mg-btn ghost" onClick={() => setSheet({ kind: 'history' })} title="Historia ruchów">
              <Icon name="history" size={18} /> Historia
            </button>
            <button type="button" className="mg-btn ghost" onClick={() => onPrint({ title: 'Stan magazynu', run: printStockSummary })} title="Drukuj stan magazynu">
              <Icon name="printer" size={18} /> Drukuj stan
            </button>
            {allowPdf && (
              <button type="button" className="mg-btn ghost" onClick={() => openStockPdf({ kind: 'summary' }).catch((e) => notify(stockErrorText(e), 'err'))} title="Stan magazynu jako PDF">
                <Icon name="file-text" size={18} /> PDF
              </button>
            )}
          </div>
        </div>

        {/* Filtry (nie w Dobraniu — tam jest lista) */}
        {mode !== 'pick' && (
          <div className="mg-filters">
            <label className="mg-search">
              <Icon name="search" size={17} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj: nazwa, SKU, kod…" aria-label="Szukaj produktu" />
              {q && <button type="button" className="mg-x sm" onClick={() => setQ('')} aria-label="Wyczyść"><Icon name="x" size={15} /></button>}
            </label>
            <div className="mg-chips">
              {(['active', 'short', 'all'] as Filter[]).map((f) => (
                <button key={f} type="button" className="mg-chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                  {f === 'active' ? 'Aktywne' : f === 'short' ? 'Brakuje' : 'Wszystkie'}
                  <span className={`mg-cnt${f === 'short' && counts.short > 0 ? ' err' : ''}`}>{counts[f]}</span>
                </button>
              ))}
              <button type="button" className="mg-chip sort" onClick={changeSort} title="Zmień kolejność">
                <Icon name="arrow-up-down" size={15} /> {SORT_LABEL[sort]}
              </button>
            </div>
          </div>
        )}

        {/* Pasek narzędzi trybu */}
        {mode === 'delivery' && (
          <div className="mg-toolbar">
            <div className="mg-incs" role="radiogroup" aria-label="Krok dodawania">
              <span className="mg-tb-lbl">{compact ? 'Krok' : 'Dotknięcie dodaje'}</span>
              {(data.config.quickSteps.length ? data.config.quickSteps : [1, 5, 10]).map((s) => (
                <button key={s} type="button" className="mg-inc" aria-pressed={inc === s} onClick={() => setInc(s)}>+{s}</button>
              ))}
              <button type="button" className="mg-inc" aria-pressed={inc === 'pack'} onClick={() => setInc('pack')} title="Krok = opakowanie produktu (np. blacha 6)">
                <Icon name="package" size={15} /> Opakowanie
              </button>
            </div>
            <div className="mg-tb-right">
              <form className="mg-scan" onSubmit={onScanSubmit}>
                <Icon name="scan-line" size={18} />
                <input ref={scanRef} value={scanVal} onChange={(e) => setScanVal(e.target.value)} placeholder="Skanuj kod…" aria-label="Kod produktu" />
              </form>
              <button type="button" className="mg-btn accent" onClick={fillByDemand} disabled={!demandKnown || t.toTake === 0}>
                <Icon name="wand-sparkles" size={18} /> {compact ? 'Wypełnij braki' : 'Wypełnij wg zapotrzebowania'}
              </button>
            </div>
          </div>
        )}

        {mode === 'remove' && (
          <div className="mg-toolbar">
            <div className="mg-incs">
              <span className="mg-tb-lbl">Powód</span>
              {data.config.removalReasons.map((r) => (
                <button key={r} type="button" className="mg-inc reason" aria-pressed={drafts.reason === r} onClick={() => setDrafts((d) => ({ ...d, reason: r }))}>{r}</button>
              ))}
            </div>
            <div className="mg-incs">
              <span className="mg-tb-lbl">Krok</span>
              {(data.config.quickSteps.length ? data.config.quickSteps : [1, 5]).slice(0, 3).map((s) => (
                <button key={s} type="button" className="mg-inc" aria-pressed={inc === s} onClick={() => setInc(s)}>−{s}</button>
              ))}
            </div>
          </div>
        )}

        {mode === 'count' && (
          <div className="mg-tip">
            <Icon name="info" size={16} /> <span>Dotknij produktu i wpisz, ile <b>naprawdę leży</b> na półce — różnicę policzę sam.</span>
          </div>
        )}

        {/* Treść trybu */}
        {mode === 'pick' ? (
          openRun && pd ? (
            <SettleView
              run={openRun}
              draft={pd}
              items={items}
              byKey={byKey}
              kiosk={kiosk}
              update={updatePick}
              openKeypad={setKeypad}
              onAdd={() => setSheet({ kind: 'picker' })}
            />
          ) : (
            <PickList lines={pickLines} demandKnown={demandKnown} onItem={(k) => setSheet({ kind: 'item', key: k })} />
          )
        ) : visible.length === 0 ? (
          <div className="mg-empty small">
            <Icon name={needle ? 'search' : filter === 'short' ? 'circle-check' : 'inbox'} size={28} />
            <p>
              {needle ? `Nic nie pasuje do „${q}”.` : filter === 'short' ? 'Niczego nie brakuje.' : filter === 'active' ? 'Brak aktywnych produktów — pokaż wszystkie.' : 'Brak produktów w katalogu magazynu.'}
            </p>
            {filter !== 'all' && <button type="button" className="mg-btn ghost" onClick={() => { setFilter('all'); setQ(''); }}>Pokaż wszystkie</button>}
          </div>
        ) : (
          <div className="mg-grid">
            {visible.map((it) => (
              <ItemTile
                key={it.key}
                it={it}
                mode={mode}
                demandKnown={demandKnown}
                draft={draftForMode[it.key]}
                step={stepOf(it)}
                pulse={pulse && pulse.key === it.key ? pulse.n : 0}
                onTap={() => {
                  if (mode === 'stock') { setSheet({ kind: 'item', key: it.key }); return; }
                  sfx('click');
                  if (mode === 'count') { openKeypadFor('count', it); return; }
                  setQty(mode, it.key, (draftForMode[it.key] || 0) + stepOf(it));
                  bumpPulse(it.key);
                }}
                onMinus={() => {
                  sfx('click');
                  if (mode === 'count') { setQty('count', it.key, null); return; }
                  if (mode === 'delivery' || mode === 'remove') setQty(mode, it.key, (draftForMode[it.key] || 0) - stepOf(it));
                }}
                onNumber={() => { if (mode === 'delivery' || mode === 'remove' || mode === 'count') openKeypadFor(mode, it); }}
              />
            ))}
          </div>
        )}
        <div className="mg-bottom-space" />
      </div>

      {/* Pasek „Cofnij" po zapisie */}
      {undo && (
        <div className="mg-undo" key={undo.batchId}>
          <Icon name="circle-check" size={20} />
          <span className="mg-undo-t">{undo.text}</span>
          <button type="button" className="mg-btn ghost sm" onClick={doUndo}><Icon name="undo-2" size={16} /> Cofnij</button>
          <button type="button" className="mg-x sm" onClick={() => setUndo(null)} aria-label="Zamknij"><Icon name="x" size={16} /></button>
          <i className="mg-undo-bar" style={{ animationDuration: `${UNDO_MS}ms` }} />
        </div>
      )}

      {/* Stopka trybu */}
      <Footer
        mode={mode}
        compact={!!compact}
        busy={busy}
        drafts={drafts}
        byKey={byKey}
        openRun={openRun}
        pickDraft={pd}
        pickTotal={demandKnown ? pickTotal : 0 /* bez UCS lista byłaby z samego zapasu — PickList i tak mówi „poczekaj na UCS” */}
        pickCount={pickLines.length}
        allowPdf={!!allowPdf}
        onSaveDelivery={saveDelivery}
        onAskRemove={() => setConfirm('remove')}
        onSaveCount={saveCount}
        onClear={(w) => clearDraft(w)}
        onPrintPick={printPickList}
        onCreateNoPrint={createRunNoPrint}
        onSettle={settle}
        onCancelRun={() => setConfirm('cancelRun')}
        onReprint={() => openRun && onPrint({ title: 'Do dobrania', subtitle: `Lista #${runCode(openRun.id)}`, run: () => printStockRun(openRun.id) })}
        onPdf={() => openRun && openStockPdf({ kind: 'pick', runId: openRun.id }).catch((e) => notify(stockErrorText(e), 'err'))}
        settleLines={openRun && pd ? settleLines(openRun, pd) : []}
      />

      {/* Arkusze i okna */}
      {keypad && <Keypad req={keypad} onClose={() => setKeypad(null)} />}
      {sheet && sheet.kind === 'history' && <HistorySheet onClose={() => setSheet(null)} onChanged={() => { refresh(); }} notify={notify} />}
      {sheetItem && <ItemSheet item={sheetItem} overview={data} onClose={() => setSheet(null)} onAction={(m) => itemAction(sheetItem, m)} />}
      {sheet && sheet.kind === 'picker' && openRun && pd && (
        <ItemPickerSheet
          items={items}
          exclude={openRun.lines.map((l) => l.key).concat(Object.keys(pd.extra))}
          onClose={() => setSheet(null)}
          onPick={(it) => {
            setSheet(null);
            updatePick((p) => ({ ...p, extra: { ...p.extra, [it.key]: Math.max(1, it.step || 1) } }));
          }}
        />
      )}
      {confirm === 'remove' && (
        <Confirm
          title={`Zdjąć ${sumQty(drafts.remove)} szt. ze stanu?`}
          desc={<>Powód: <b>{drafts.reason}</b>. {Object.keys(drafts.remove).map((k) => `${byKey[k] ? byKey[k].name : k} ×${drafts.remove[k]}`).join(', ')}</>}
          confirmLabel="Zdejmij ze stanu"
          tone="danger"
          busy={busy}
          onConfirm={saveRemove}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'cancelRun' && openRun && (
        <Confirm
          title={`Anulować listę #${runCode(openRun.id)}?`}
          desc="Nic nie trafi na stan. Użyj, gdy lista jest nieaktualna albo nikt nie schodził do piekarni."
          confirmLabel="Anuluj listę"
          tone="danger"
          onConfirm={cancelRun}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/* =================================================================== */

/** Liczba z „podskokiem" przy każdej zmianie. */
function Bump({ v }: { v: number }) {
  return <span key={v} className="mg-bump">{v}</span>;
}

function Disabled({ variant }: { variant: 'kiosk' | 'admin' }) {
  return (
    <div className={`mg-board mg-${variant}`}>
      <div className="mg-empty">
        <span className="mg-empty-ico"><Icon name="warehouse" size={30} /></span>
        <h3>Magazyn jest wyłączony</h3>
        <p>Włączysz go w panelu admina: <b>Ustawienia → Magazyn</b>.</p>
      </div>
    </div>
  );
}

function BoardSkeleton({ variant, compact }: { variant: 'kiosk' | 'admin'; compact?: boolean }) {
  return (
    <div className={`mg-board mg-${variant}${compact ? ' mg-compact' : ''}`} aria-busy="true">
      <div className="mg-scroll">
        <div className="mg-skel" style={{ height: 18, width: 260, marginBottom: 14 }} />
        <div className="mg-kpis">{[0, 1, 2, 3].map((i) => <div key={i} className="mg-skel" style={{ height: variant === 'kiosk' ? 104 : 88 }} />)}</div>
        <div className="mg-skel" style={{ height: variant === 'kiosk' ? 60 : 44, margin: '16px 0' }} />
        <div className="mg-grid">{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <div key={i} className="mg-skel" style={{ height: variant === 'kiosk' ? 190 : 160 }} />)}</div>
      </div>
    </div>
  );
}

/* ---------- kafelek produktu ---------- */

function ItemTile({ it, mode, demandKnown, draft, step, pulse, onTap, onMinus, onNumber }: {
  it: StockItemView;
  mode: BoardMode;
  demandKnown: boolean;
  draft: number | undefined;
  step: number;
  pulse: number;
  onTap: () => void;
  onMinus: () => void;
  onNumber: () => void;
}) {
  const st = itemStatus(it, demandKnown);
  const variant = itemVariant(it);
  const cover = it.demand > 0 ? Math.max(0, Math.min(1, Math.max(0, it.onHand) / it.demand)) : it.onHand > 0 ? 1 : 0;
  const hasDraft = draft !== undefined && (mode === 'count' || draft > 0);
  const over = mode === 'remove' && hasDraft && (draft || 0) > Math.max(0, it.onHand);
  const stop = (fn: () => void) => (e: React.SyntheticEvent) => { e.stopPropagation(); fn(); };

  let badge: React.ReactNode = null;
  if (hasDraft && mode === 'delivery') badge = <>+{draft}</>;
  if (hasDraft && mode === 'remove') badge = <>−{draft}</>;
  if (hasDraft && mode === 'count') badge = <>{draft}</>;

  return (
    <div
      id={`mg-item-${it.key}`}
      role="button"
      tabIndex={0}
      className={`mg-tile tone-${st.tone} m-${mode}${hasDraft ? ' has-draft' : ''}${over ? ' over' : ''}${pulse ? ' pulse' : ''}`}
      data-pulse={pulse}
      title={mode === 'delivery' ? `Dotknij: +${step}` : mode === 'remove' ? `Dotknij: −${step}` : undefined}
      onClick={onTap}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); } }}
    >
      {pulse ? <span key={pulse} className="mg-tile-flash" /> : null}
      <div className="mg-tile-top">
        <span className={`mg-ico tone-${st.tone}`}><Icon name={itemIconName(it.icon)} size={22} /></span>
        {mode === 'stock' || !hasDraft ? (
          demandKnown || st.tone !== 'none' ? <span className={`mg-status tone-${st.tone}`}>{st.label}</span> : null
        ) : null}
        {hasDraft && (
          <div className={`mg-draft m-${mode}`} onClick={stop(() => undefined)}>
            <button type="button" className="mg-draft-minus" onClick={stop(onMinus)} aria-label={mode === 'count' ? 'Usuń wynik' : 'Mniej'}>
              <Icon name={mode === 'count' ? 'x' : 'minus'} size={18} />
            </button>
            <button type="button" className="mg-draft-val tnum" onClick={stop(onNumber)} aria-label="Wpisz liczbę">
              <span key={String(draft)} className="mg-bump">{badge}</span>
            </button>
          </div>
        )}
      </div>
      <div className="mg-tile-name">{itemTitle(it)}</div>
      <div className="mg-tile-sub">
        {variant && <span>{variant}</span>}
        {variant && it.sku && <span className="dot">·</span>}
        {it.sku && <span className="mono">{it.sku}</span>}
      </div>
      <div className="mg-tile-qty">
        {mode === 'count' && hasDraft ? (
          <div className="mg-count-line">
            <span className="was">było <b className="tnum">{it.onHand}</b></span>
            <Icon name="chevron-right" size={16} />
            <span className="is">jest <b className="tnum">{draft}</b></span>
            <span className={`diff ${(draft || 0) - it.onHand === 0 ? '' : (draft || 0) - it.onHand > 0 ? 'plus' : 'minus'}`}>({signed((draft || 0) - it.onHand)})</span>
          </div>
        ) : (
          <>
            <span className={`mg-big tnum${it.onHand < 0 ? ' neg' : ''}`}><span key={it.onHand} className="mg-bump">{it.onHand}</span></span>
            <span className="mg-tile-need">
              {mode === 'delivery' && hasDraft ? (
                <>będzie <b className="tnum">{it.onHand + (draft || 0)}</b></>
              ) : demandKnown && it.demand > 0 ? (
                <>potrzeba <b className="tnum">{it.demand}</b> · {it.ordersCount} zam.</>
              ) : (
                <span className="dim">szt. na stanie</span>
              )}
            </span>
          </>
        )}
      </div>
      <div className="mg-bar"><i className={`tone-${st.tone}`} style={{ width: `${Math.round(cover * 100)}%` }} /></div>
      {it.onHand < 0 && mode === 'stock' && <div className="mg-tile-warn"><Icon name="triangle-alert" size={13} /> spakowano więcej, niż wpisano — dopisz dostawę</div>}
      {over && <div className="mg-tile-warn"><Icon name="triangle-alert" size={13} /> więcej niż na stanie ({it.onHand})</div>}
    </div>
  );
}

/* ---------- Dobranie: lista bez otwartej listy ---------- */

function PickList({ lines, demandKnown, onItem }: { lines: StockItemView[]; demandKnown: boolean; onItem: (key: string) => void }) {
  if (!demandKnown) {
    return (
      <div className="mg-empty small">
        <Icon name="cloud-off" size={28} />
        <p>Bez UCS nie wiem, ile potrzeba do zamówień — listę „Do dobrania” wydrukujesz, gdy połączenie wróci.</p>
      </div>
    );
  }
  if (!lines.length) {
    return (
      <div className="mg-empty">
        <span className="mg-empty-ico ok big"><Icon name="check" size={40} /></span>
        <h3>Niczego nie brakuje</h3>
        <p>Zapas pokrywa wszystkie zamówienia.</p>
      </div>
    );
  }
  return (
    <div className="mg-picklist">
      <div className="mg-sec-head"><Icon name="clipboard-list" size={16} /> Do przyniesienia z piekarni</div>
      {lines.map((it) => (
        <button type="button" key={it.key} className="mg-pl-row" onClick={() => onItem(it.key)}>
          <span className="mg-ico tone-short"><Icon name={itemIconName(it.icon)} size={22} /></span>
          <span className="mg-pl-text">
            <b>{itemTitle(it)}</b>
            <small>{itemVariant(it) ? `${itemVariant(it)} · ` : ''}potrzeba {it.demand} · na stanie {it.onHand}{it.safetyStock ? ` · zapas ${it.safetyStock}` : ''}</small>
          </span>
          <span className="mg-pl-qty"><small>dobrać</small><b className="tnum">{it.toTake}</b></span>
        </button>
      ))}
    </div>
  );
}

/* ---------- Dobranie: rozliczenie otwartej listy ---------- */

function SettleView({ run, draft, items, byKey, kiosk, update, openKeypad, onAdd }: {
  run: StockRun;
  draft: PickDraft;
  items: StockItemView[];
  byKey: Record<string, StockItemView>;
  kiosk: boolean;
  update: (fn: (p: PickDraft) => PickDraft) => void;
  openKeypad: (r: KeypadRequest) => void;
  onAdd: () => void;
}) {
  // Od wydruku doszły zamówienia? Porównujemy bieżące „do dobrania" z listą.
  const planned: Record<string, number> = {};
  run.lines.forEach((l) => { planned[l.key] = l.quantity; });
  const drift = items.some((i) => i.toTake > (planned[i.key] || 0));
  const allOn = draft.off.length === 0;
  const click = () => { if (kiosk) AudioFX.click(); };

  const setLine = (key: string, n: number, plannedQty: number) =>
    update((p) => {
      const qty = { ...p.qty };
      if (n === plannedQty) delete qty[key];
      else qty[key] = Math.max(0, n);
      const off = n <= 0 ? (p.off.indexOf(key) < 0 ? p.off.concat(key) : p.off) : p.off.filter((k) => k !== key);
      if (n <= 0) delete qty[key];
      return { ...p, qty, off };
    });
  const setExtra = (key: string, n: number) =>
    update((p) => {
      const extra = { ...p.extra };
      if (n <= 0) delete extra[key];
      else extra[key] = n;
      return { ...p, extra };
    });

  return (
    <div className="mg-settle">
      <div className="mg-settle-head">
        <span className="mg-code">#{runCode(run.id)}</span>
        <div>
          <div className="mg-settle-title">Rozlicz dobranie</div>
          <div className="mg-settle-sub">
            {timePl(run.createdAt)}{run.createdByName ? ` · ${run.createdByName}` : ''} · {run.totalUnits} szt. w {run.lines.length} poz.
          </div>
        </div>
        <button
          type="button"
          className="mg-btn ghost sm"
          onClick={() => { click(); update((p) => ({ ...p, off: allOn ? run.lines.map((l) => l.key) : [], qty: {} })); }}
        >
          <Icon name={allOn ? 'square' : 'square-check-big'} size={16} /> {allOn ? 'Odznacz wszystko' : 'Zaznacz wszystko'}
        </button>
      </div>
      <div className="mg-tip"><Icon name="info" size={16} /> Odznacz, czego nie udało się wziąć. Inna liczba? Zmień ją strzałkami albo dotknij liczby.</div>
      {drift && (
        <div className="mg-note warn"><Icon name="triangle-alert" size={16} /> Od wydruku doszły zamówienia — po rozliczeniu wydrukuj nową listę.</div>
      )}
      <div className="mg-settle-list">
        {run.lines.map((l) => {
          const on = draft.off.indexOf(l.key) < 0;
          const qty = on ? (draft.qty[l.key] !== undefined ? draft.qty[l.key] : l.quantity) : 0;
          const it = byKey[l.key];
          const changed = on && qty !== l.quantity;
          return (
            <div key={l.key} className={`mg-sl${on ? '' : ' off'}${changed ? ' changed' : ''}`}>
              <button
                type="button"
                className={`mg-check${on ? ' on' : ''}`}
                aria-pressed={on}
                aria-label={on ? 'Odznacz' : 'Zaznacz'}
                onClick={() => { click(); setLine(l.key, on ? 0 : l.quantity, l.quantity); }}
              >
                <Icon name="check" size={kiosk ? 28 : 20} />
              </button>
              <button type="button" className="mg-sl-text" onClick={() => { click(); setLine(l.key, on ? 0 : l.quantity, l.quantity); }}>
                <b>{it ? itemTitle(it) : l.name}</b>
                <small>{l.variantTitle ? `${l.variantTitle} · ` : ''}plan {l.quantity}{changed ? ` → wzięto ${qty}` : ''}</small>
              </button>
              <Stepper
                value={qty}
                onMinus={() => { click(); setLine(l.key, qty - 1, l.quantity); }}
                onPlus={() => { click(); setLine(l.key, qty + 1, l.quantity); }}
                onTap={() => openKeypad({
                  title: `Wzięto: ${it ? itemTitle(it) : l.name}`,
                  sub: `plan ${l.quantity}`,
                  icon: itemIconName(it?.icon),
                  initial: qty,
                  suggestions: [{ label: `Plan: ${l.quantity}`, value: l.quantity }, { label: 'Nic', value: 0 }],
                  onDone: (n) => setLine(l.key, n, l.quantity),
                })}
              />
            </div>
          );
        })}
        {Object.keys(draft.extra).map((k) => {
          const it = byKey[k];
          const qty = draft.extra[k];
          return (
            <div key={k} className="mg-sl extra">
              <span className="mg-check on extra"><Icon name="plus" size={kiosk ? 26 : 18} /></span>
              <div className="mg-sl-text">
                <b>{it ? itemTitle(it) : k}</b>
                <small>spoza listy</small>
              </div>
              <Stepper
                value={qty}
                onMinus={() => { click(); setExtra(k, qty - 1); }}
                onPlus={() => { click(); setExtra(k, qty + 1); }}
                onTap={() => openKeypad({
                  title: `Wzięto: ${it ? itemTitle(it) : k}`,
                  sub: 'spoza listy',
                  icon: itemIconName(it?.icon),
                  initial: qty,
                  onDone: (n) => setExtra(k, n),
                })}
              />
            </div>
          );
        })}
        <button type="button" className="mg-sl-add" onClick={onAdd}><Icon name="plus" size={18} /> Dodaj pozycję spoza listy</button>
      </div>
    </div>
  );
}

function Stepper({ value, onMinus, onPlus, onTap }: { value: number; onMinus: () => void; onPlus: () => void; onTap: () => void }) {
  return (
    <div className="mg-stepper">
      <button type="button" onClick={onMinus} disabled={value <= 0} aria-label="Mniej"><Icon name="minus" size={20} /></button>
      <button type="button" className="v tnum" onClick={onTap} aria-label="Wpisz liczbę"><span key={value} className="mg-bump">{value}</span></button>
      <button type="button" onClick={onPlus} aria-label="Więcej"><Icon name="plus" size={20} /></button>
    </div>
  );
}

/* ---------- stopka ---------- */

function Footer(p: {
  mode: BoardMode;
  compact: boolean;
  busy: boolean;
  drafts: Drafts;
  byKey: Record<string, StockItemView>;
  openRun: StockRun | null;
  pickDraft: PickDraft | null;
  pickTotal: number;
  pickCount: number;
  allowPdf: boolean;
  settleLines: Array<{ key: string; quantity: number }>;
  onSaveDelivery: () => void;
  onAskRemove: () => void;
  onSaveCount: () => void;
  onClear: (w: 'delivery' | 'remove' | 'count') => void;
  onPrintPick: () => void;
  onCreateNoPrint: () => void;
  onSettle: () => void;
  onCancelRun: () => void;
  onReprint: () => void;
  onPdf: () => void;
}) {
  const { mode, drafts, busy } = p;
  if (mode === 'stock') return null;

  if (mode === 'delivery') {
    const units = sumQty(drafts.delivery);
    const n = countQty(drafts.delivery);
    return (
      <div className="mg-footer">
        <div className="mg-f-sum">
          {units > 0 ? <><span className="mg-f-big tnum"><Bump v={units} /> szt.</span><span className="mg-f-dim">Dostawa · {n} poz.</span></> : <span className="mg-f-dim">Dotknij produktu, by dodać do dostawy</span>}
        </div>
        {units > 0 && <button type="button" className="mg-btn ghost" onClick={() => p.onClear('delivery')}><Icon name="eraser" size={18} /> {!p.compact && 'Wyczyść'}</button>}
        <button type="button" className="mg-btn success lg" disabled={!units || busy} onClick={p.onSaveDelivery}>
          <Icon name="check" size={20} /> Zapisz dostawę
        </button>
      </div>
    );
  }

  if (mode === 'remove') {
    const units = sumQty(drafts.remove);
    return (
      <div className="mg-footer">
        <div className="mg-f-sum">
          {units > 0 ? <><span className="mg-f-big tnum">−<Bump v={units} /> szt.</span><span className="mg-f-dim">{drafts.reason || 'wybierz powód'}</span></> : <span className="mg-f-dim">Dotknij produktu, by zdjąć go ze stanu</span>}
        </div>
        {units > 0 && <button type="button" className="mg-btn ghost" onClick={() => p.onClear('remove')}><Icon name="eraser" size={18} /> {!p.compact && 'Wyczyść'}</button>}
        <button type="button" className="mg-btn danger lg" disabled={!units || !drafts.reason || busy} onClick={p.onAskRemove}>
          <Icon name="package-minus" size={20} /> {units > 0 ? `Zdejmij ${units} szt.` : 'Zdejmij'}
        </button>
      </div>
    );
  }

  if (mode === 'count') {
    const n = Object.keys(drafts.count).length;
    const diff = Object.keys(drafts.count).filter((k) => p.byKey[k] && p.byKey[k].onHand !== drafts.count[k]).length;
    return (
      <div className="mg-footer">
        <div className="mg-f-sum">
          {n > 0 ? <><span className="mg-f-big tnum">Korekta: {n} poz.</span><span className="mg-f-dim">{diff ? `${diff} z różnicą` : 'bez różnic'}</span></> : <span className="mg-f-dim">Dotknij produktu i wpisz, ile jest</span>}
        </div>
        {n > 0 && <button type="button" className="mg-btn ghost" onClick={() => p.onClear('count')}><Icon name="eraser" size={18} /> {!p.compact && 'Wyczyść'}</button>}
        <button type="button" className="mg-btn primary lg" disabled={!n || busy} onClick={p.onSaveCount}>
          <Icon name="check" size={20} /> Zapisz spis
        </button>
      </div>
    );
  }

  // Dobranie
  if (p.openRun) {
    const taken = p.settleLines.reduce((s, l) => s + l.quantity, 0);
    return (
      <div className="mg-footer">
        <div className="mg-f-sum">
          <span className="mg-f-big tnum"><Bump v={taken} /> <span className="mg-f-of">z {p.openRun.totalUnits} szt.</span></span>
          <span className="mg-f-dim">Przyjmij dobrane</span>
        </div>
        <button type="button" className="mg-btn ghost" onClick={p.onCancelRun} title="Anuluj listę"><Icon name="trash-2" size={18} /> {!p.compact && 'Anuluj listę'}</button>
        <button type="button" className="mg-btn ghost" onClick={p.onReprint} title="Drukuj ponownie"><Icon name="printer" size={18} /> {!p.compact && 'Drukuj ponownie'}</button>
        {p.allowPdf && <button type="button" className="mg-btn ghost" onClick={p.onPdf}><Icon name="file-text" size={18} /> PDF</button>}
        <button type="button" className="mg-btn success lg" disabled={busy} onClick={p.onSettle}>
          <Icon name="clipboard-check" size={20} /> Przyjmij do stanu
        </button>
      </div>
    );
  }
  if (!p.pickTotal) return null;
  return (
    <div className="mg-footer">
      <div className="mg-f-sum">
        <span className="mg-f-big tnum">{p.pickTotal} szt.</span>
        <span className="mg-f-dim">do dobrania · {p.pickCount} poz.</span>
      </div>
      {p.allowPdf && (
        <button type="button" className="mg-btn ghost" disabled={busy} onClick={p.onCreateNoPrint}><Icon name="file-text" size={18} /> Bez druku + PDF</button>
      )}
      <button type="button" className="mg-btn primary lg" disabled={busy} onClick={p.onPrintPick}>
        <Icon name="printer" size={20} /> Drukuj listę „Do dobrania”
      </button>
    </div>
  );
}
